<script lang="ts">
  import { hasMinPremiumSubscription } from "../../helpers/subscription";
  import { uploadPDFInvoices } from "../../helpers/mass-upload-api";
  import Text from "../../components/Text.svelte";
  import LargeButton from "../../components/buttons/LargeButton.svelte";
  import Stack from "../../components/Stack.svelte";
  import AddFileIcon from "../../components/icons/AddFileIcon.svelte";
  import ArrowLeft from "../../components/icons/ArrowLeft.svelte";
  import CloseIcon from "../../components/icons/CloseIcon.svelte";
  import FileDark from "../../components/icons/FileDark.svelte";
  import Checkmark from "../../components/icons/Checkmark.svelte";
  import Paywall from "../Paywall/Paywall.svelte";
  import TextNew from "../../components/TextNew.svelte";
  import { createGetSubscriptionQuery } from "../../queries/subscriptions";
  import { type SubscriptionModalOption } from "../../stores/subscription-modal-store";
  import { onMount } from "svelte";
  import { track } from "../../helpers/mixpanel";
  import DragDrop from "../../components/DragDrop.svelte";
  import { selectedBuyer } from "../../stores/buyer";

  let input: HTMLInputElement;
  let files: File[] = [];
  let loading = false;
  let buttonTitle = "upload";
  let buttonColor = "accent";
  let isHovered = false;
  let showExplainer = false;

  export let variant: SubscriptionModalOption;
  export let onComplete: () => void;
  export let onClose: () => void;

  $: buyerID = $selectedBuyer?.id ?? "";
  $: subscriptionQuery = createGetSubscriptionQuery(buyerID);
  $: hasPremium =
    $subscriptionQuery.data != null &&
    hasMinPremiumSubscription($subscriptionQuery.data?.plan, $subscriptionQuery.data?.status);
  $: invoiceEmail = $subscriptionQuery.data?.email_alias;

  const buyer = $selectedBuyer;

  function handleMouseEnter() {
    isHovered = true;
  }

  function handleMouseLeave() {
    isHovered = false;
  }

  function triggerUploadInput() {
    input.click();
    track("upload_invoice_modal_click");
  }

  function updateFiles(event: CustomEvent<File[]>) {
    files = event.detail;
  }

  function chooseFiles() {
    if (!input.files) return;

    files = [...files, ...Array.from(input.files)];
  }

  async function confirmUpload() {
    if (!buyer?.id) {
      alert("Error: missing buyer id");
      return;
    }
    loading = true;

    const formData = new FormData();
    formData.append("buyer_id", buyer.id);
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      await uploadPDFInvoices(formData);
      buttonColor = "success";
      buttonTitle = "done";
      track("upload_invoice_success_bkd");

      setTimeout(() => {
        onComplete();
      }, 500);
    } catch (error) {
      buttonColor = "dark";
      buttonTitle = "try again";
      track("upload_invoice_error_bkd");
    }

    loading = false;
  }

  function handleShowExplainer() {
    showExplainer = true;
    track("upload_auto_fwd_info_click");
  }

  function handleHideExplainer() {
    showExplainer = false;
  }

  onMount(() => {
    if (variant === "forwarding-setup") {
      showExplainer = true;
    }
  });

  $: if (!showExplainer && !hasPremium) {
    track("upload_invoice_modal_view");
  }
</script>

{#if showExplainer}
  <div class="container">
    <div class="info">
      {#if variant !== "forwarding-setup"}
        <button on:click={handleHideExplainer} class="icon-button">
          <ArrowLeft />
        </button>
      {/if}
      <TextNew weight="medium" size="l" --rk-text-color="var(--rekki-neutral-04)"
        >set up auto forwarding</TextNew
      >

      <div class="spacer" />
      <button on:click={onClose} class="icon-button">
        <CloseIcon />
      </button>
    </div>

    <video
      autoplay
      loop
      muted
      playsinline
      class="instructions-video"
      src="static/assets/forward-email.webm"
    />

    <div class="instructions">
      <div class="instructions-copy">
        <TextNew size="s" weight="medium">instructions for Gmail</TextNew>
        <a
          href="https://rekki.my.site.com/help/s/article/Invoice-Auto-Forwarding-Setup-Instructions"
          target="_blank"
          rel="noopener noreferrer"
          on:click={() => {
            track("upload_auto_fwd_provider_click");
          }}
        >
          <TextNew size="xs" --rk-text-color="var(--rk-text-transparency-dark)"
            >+ more detailed instructions</TextNew
          >
        </a>
      </div>
      <ol>
        <li>
          <TextNew size="s" --text-display="inline">
            click on the ‘Show search options’ icon in the search bar
          </TextNew>
        </li>
        <li>
          <TextNew size="s" --text-display="inline"
            >enter your criteria, e.g. From = supplier email, Subject = ‘invoice’ or ‘credit note’</TextNew
          >
        </li>
        <li>
          <TextNew size="s" --text-display="inline"
            >click ‘Create filter’ at the bottom of the pop-up</TextNew
          >
        </li>
        <li>
          <TextNew size="s" --text-display="inline"
            >add your unique email address next to the ‘Forward it’ option: <span class="email"
              >{invoiceEmail}</span
            ></TextNew
          >
        </li>
        <li>
          <TextNew size="s" --text-display="inline">click ‘Create filter’</TextNew>
        </li>
      </ol>
    </div>
  </div>
{:else if !hasPremium}
  <Paywall {onClose} />
{:else}
  <div class="container">
    <div class="info">
      <Text style="font-weight: bold;" size="large" color="neutral-04">upload invoice</Text>
      <div class="spacer" />
      <button on:click={onClose} class="icon-button">
        <CloseIcon />
      </button>
    </div>

    <DragDrop
      on:dragStart={handleMouseEnter}
      on:dragEnd={handleMouseLeave}
      on:files={updateFiles}
      on:click={triggerUploadInput}
    >
      <div class="section bordered" class:add-additional={files.length}>
        {#if files.length === 0}
          <Stack horizontal alignItems="center" padding="0 0 24px 0">
            <div class="add-file-icon" class:is-hovered={isHovered}>
              <AddFileIcon />
            </div>
          </Stack>
          <Text size="medium-large" color="neutral-03" align="center" style="white-space:pre-line;"
            >{"click to select from computer\n\n(PDF files only)"}</Text
          >
        {:else}
          <Text size="medium-large" color="neutral-03" align="center"
            >{"click to select files"}</Text
          >
        {/if}
      </div>
    </DragDrop>

    <div class="section">
      <div class="file-input-wrapper">
        <input
          type="file"
          id="fileInput"
          name="fileInput"
          accept="application/pdf"
          bind:this={input}
          on:change={chooseFiles}
          multiple
        />
      </div>
    </div>

    {#if files.length}
      <div class="files-list">
        {#each files as file}
          <div class="file">
            <FileDark />
            <Text size="medium" color="neutral-04" style="padding-left: 4px;">{file.name}</Text>
          </div>
        {/each}
      </div>

      <div class="upload-button-container">
        <!-- help I cant seem to figure out how to get just the icon slot to render conditionally -->
        {#if buttonColor === "success"}
          <LargeButton
            title={buttonTitle}
            color={buttonColor}
            loadingInline={loading}
            square={true}
            onClick={confirmUpload}
          >
            <Checkmark slot="icon" />
          </LargeButton>
        {:else}
          <LargeButton
            title={buttonTitle}
            color={buttonColor}
            loadingInline={loading}
            square={true}
            onClick={confirmUpload}
          />
        {/if}
      </div>
    {:else}
      <div class="explainer">
        <div class="explainer-copy">
          <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)"
            >did you know?</TextNew
          >
          <TextNew size="s">you can auto forward your invoices straight to REKKI</TextNew>
        </div>
        <button on:click={handleShowExplainer} class="explainer-cta"> find out how </button>
      </div>
    {/if}
  </div>
{/if}

<style>
  input {
    font-size: 18px;
  }

  .container {
    background-color: #ffffff;
    padding: 16px 16px 24px 16px;
    flex: 0;
    border-radius: 8px;
    overflow: hidden;
    min-width: 660px;
  }

  .files-list {
    position: relative;
    margin: 16px 0 0 0;
    padding-bottom: 16px;
    max-height: 344px;
    overflow-y: auto;
  }

  .upload-button-container {
    position: relative;
    bottom: 0px;
    display: flex;
    justify-content: center;
  }

  .file {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 16px;
    background-color: var(--rekki-neutral-00);
    border-radius: 8px;
    margin-bottom: 4px;
  }

  .files-list::after {
    position: relative;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100px; /* Adjust the height as needed */
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(20, 20, 20, 1));

    z-index: 1; /* Set a higher z-index */
  }

  .info {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 24px;
  }

  .spacer {
    flex: 1;
  }

  .icon-button {
    display: grid;
    place-items: center;
  }

  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .explainer {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background: #ddecfc;
    padding: 12px 16px;
    border-radius: 12px;
    margin-top: 12px;
  }

  .explainer-copy {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .explainer-cta {
    width: 135px;
    height: 48px;
    border-radius: 100px;
    background: var(--rekki-light);
    color: var(--rekki-dark);
    font-size: 16px;
    font-weight: bold;
  }

  .instructions-video {
    width: 100%;
    height: 380px;
    border-radius: 12px;
    box-shadow: 0px 0px 0px 1px #ebebeb;
  }

  .instructions {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
    margin-top: 12px;
  }

  .instructions-copy {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .instructions a {
    text-decoration: none;
  }

  .instructions ol li {
    list-style-position: inside;
  }

  .email {
    text-decoration: underline;
  }

  .bordered {
    padding: 80px;
    border: dashed 2px #d7d6d6;
    border-radius: 16px;
  }

  .add-additional {
    padding: 40px 80px;
  }

  .section .add-file-icon {
    transition: transform 570ms cubic-bezier(0.68, -0.55, 0.27, 1.55);
  }

  .section:hover .add-file-icon {
    transform: rotate(-5deg) scale(1.1);
  }
  /* File input styles */
  .file-input-wrapper {
    position: relative;
    overflow: hidden;
    display: inline-block;
  }

  .file-input-wrapper input[type="file"] {
    font-size: 100px;
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
  }
</style>
